import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { isBlueberryFeature } from '../../services/data/impl/cube/cubeUtils';
import useScanData from '../scanDataApi/hooks/useScanData';
import { selectCurrFeature } from '../select/selectSlice';
import { BlueberryPhotoCount } from './BlueberryPhotoCount';
import ImageInfoSlot from './ImageInfoSlot';
import { NumericSlot } from './slots/NumericSlot';

const placeholder = '--';

const ImageInfo: React.FC = () => {
  const theme = useTheme();
  const currFeature = useAppSelector(selectCurrFeature);
  const { infoSlots } = useScanData();
  const [isExpanded, setExpanded] = useState(true);
  const [previousSlots, setPreviousSlots] = useState<typeof infoSlots.data>([
    { label: placeholder, children: null },
    { label: placeholder, children: null },
    { label: placeholder, children: null },
  ]);

  const handleExpandClick = () => {
    setExpanded(!isExpanded);
  };

  const isBlueberry = useMemo(
    () => isBlueberryFeature(currFeature),
    [currFeature]
  );

  // Update previous slots when new valid data arrives
  useEffect(() => {
    if (infoSlots.data?.length) {
      setPreviousSlots(infoSlots.data);
    }
  }, [infoSlots.data]);

  // Use actual data if available, otherwise use previous labels to avoid the UI jumping between blanks and data
  const content = useMemo(() => {
    const slotsToRender =
      infoSlots.data ||
      previousSlots?.map((slot) => ({
        label: slot.label,
        children: <NumericSlot value={null} />,
      }));

    return slotsToRender?.map((slot, i) => (
      <ImageInfoSlot
        key={i}
        label={slot.label}
        loading={infoSlots.isLoading}
        error={infoSlots.error}
        children={infoSlots.data ? slot.children : <NumericSlot value={null} />}
      />
    ));
  }, [infoSlots.data, infoSlots.isLoading, infoSlots.error, previousSlots]);

  if (isBlueberry) {
    return (
      <Box sx={{ margin: '1rem' }}>
        <BlueberryPhotoCount />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'auto',
      }}
    >
      {isExpanded && (
        <Box
          sx={{
            width: '100%',
            minHeight: '10px',
            bgcolor: theme.colors.darkTheme.grey.medium,
            color: theme.colors.common.white,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            opacity: 0.85,
          }}
        >
          {content}
        </Box>
      )}
      <IconButton
        size='small'
        sx={{
          bgcolor: theme.colors.darkTheme.grey.medium,
          borderRadius: '0 0 16px 16px',
          width: '100px',
          height: '24px',
          color: theme.colors.common.white,
          opacity: 0.85,
          ':hover': {
            bgcolor: theme.colors.darkTheme.grey.light,
          },
        }}
        onClick={handleExpandClick}
      >
        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
    </Box>
  );
};

export default ImageInfo;
